import { AuthStorage } from "utils/auth-storage";
import React, { useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Button, Divider, Modal, Upload, message } from "antd";
import { useParams } from "react-router-dom";
import { ProjectServices } from "apis/Services/ProjectService";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import ReviewScheduleFile from "./review-schedule-file";
import ScheduleColumnMappingModal from "./schedule-column-mapping-modal";
import {
  buildInitialScheduleColumnMapping,
  buildScheduleImportColumnOptionsFromRows,
  buildScheduleImportRowsFromColumnMapping,
  mapScheduleImportRowForApi,
  normalizeScheduleImportRows,
  validateScheduleColumnMapping,
} from "./scheduleImportUtils";

const { Dragger } = Upload;
const ALLOWED_SCHEDULE_UPLOAD_EXTENSIONS = new Set([
  "csv",
  "xlsx",
  "xer",
  "xml",
]);
const ALLOWED_SCHEDULE_UPLOAD_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/xml",
  "text/xml",
]);

function getFileExtension(fileName = "") {
  const normalizedFileName = String(fileName || "")
    .trim()
    .toLowerCase();
  const lastDotIndex = normalizedFileName.lastIndexOf(".");

  return lastDotIndex >= 0 ? normalizedFileName.slice(lastDotIndex + 1) : "";
}

function isAllowedScheduleUploadFile(file = {}) {
  const fileExtension = getFileExtension(file?.name);

  return (
    ALLOWED_SCHEDULE_UPLOAD_EXTENSIONS.has(fileExtension) ||
    ALLOWED_SCHEDULE_UPLOAD_MIME_TYPES.has(file?.type)
  );
}

function formatProcoreScheduleDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).trim();
  }

  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${month}-${day}-${year}`;
}

function buildProcoreScheduleRows(response) {
  const sourceRows = Array.isArray(response)
    ? response
    : Array.isArray(response?.content)
      ? response.content
      : [];

  return sourceRows
    .filter((row) => row && typeof row === "object" && !Array.isArray(row))
    .map((row) => ({
      scheduleID: row.scheduleID ?? "",
      taskName: row.taskName ?? "",
      startDate: formatProcoreScheduleDate(row.start_datetime),
      finishDate: formatProcoreScheduleDate(row.finish_datetime),
      totalFloat: row.totalFloat ?? "",
      critical: row.criticalFlag ?? "",
    }));
}

function buildMappingValidationMessage(validation = {}) {
  const validationMessages = [];

  if (validation.missingFields?.length > 0) {
    validationMessages.push(
      `Map these required columns: ${validation.missingFields.join(", ")}.`,
    );
  }

  if (validation.hasDuplicateMappings) {
    validationMessages.push(
      "Each required field must map to a different uploaded column.",
    );
  }

  return validationMessages.join(" ");
}

async function normalizeScheduleUploadResponse(response) {
  if (!response) {
    return {};
  }

  if (typeof response?.json === "function") {
    try {
      return await response.json();
    } catch (error) {
      console.error("Failed to parse schedule upload response JSON", error);
      return {};
    }
  }

  if (typeof response === "string") {
    try {
      const parsedResponse = JSON.parse(response);
      return parsedResponse && typeof parsedResponse === "object"
        ? parsedResponse
        : {};
    } catch (error) {
      console.error("Failed to parse schedule upload response string", error);
      return {};
    }
  }

  return typeof response === "object" ? response : {};
}

function parseScheduleUploadRows(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      console.error("Failed to parse schedule upload rows", error);
    }
  }

  return [];
}

function extractScheduleUploadRows(response = {}) {
  const candidateValues = [
    response?.uploadedItems,
    response?.scheduleRows,
    response?.rows,
    response?.parsedRows,
    response?.jsonData,
    response?.content,
    response,
  ];

  for (const candidateValue of candidateValues) {
    const parsedRows = parseScheduleUploadRows(candidateValue);

    if (parsedRows.length > 0) {
      return parsedRows;
    }
  }

  return [];
}

function isUploadRowObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasUploadRowObjects(items = []) {
  return Array.isArray(items) && items.some((item) => isUploadRowObject(item));
}

function isScheduleReviewResponse(response = {}) {
  return (
    hasUploadRowObjects(response?.validItems) ||
    hasUploadRowObjects(response?.invalidItems)
  );
}

function isScheduleMappingResponse(response = {}) {
  const hasUploadedItems = Array.isArray(response?.uploadedItems);
  const hasUploadedColumns = Array.isArray(response?.uploadedColsSet);

  return (
    hasUploadedItems ||
    (hasUploadedColumns && !isScheduleReviewResponse(response))
  );
}

function extractUploadErrorMessages(response = {}) {
  const rawMessages = [
    response?.message,
    ...(Array.isArray(response?.invalidItems) ? response.invalidItems : []),
  ];

  return rawMessages
    .flatMap((messageValue) =>
      Array.isArray(messageValue) ? messageValue : [messageValue],
    )
    .map((messageValue) => String(messageValue || "").trim())
    .filter(Boolean);
}

function extractScheduleUploadErrorResponse(error) {
  if (!error) {
    return {};
  }

  if (typeof error === "object" && !Array.isArray(error)) {
    if (Array.isArray(error?.uploadedItems)) {
      return error;
    }

    if (typeof error?.message === "string") {
      const jsonStartIndex = error.message.indexOf("{");

      if (jsonStartIndex >= 0) {
        try {
          const parsedErrorResponse = JSON.parse(
            error.message.slice(jsonStartIndex),
          );

          return parsedErrorResponse &&
            typeof parsedErrorResponse === "object" &&
            !Array.isArray(parsedErrorResponse)
            ? parsedErrorResponse
            : {};
        } catch (parseError) {
          console.error(
            "Failed to parse schedule upload error response",
            parseError,
          );
        }
      }
    }
  }

  return {};
}

function ImportSchedule({
  open,
  onCancel,
  onImportSuccess = () => {},
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const { company_id, id } = useParams();
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [acceptedData, setAcceptedData] = useState([]);
  const [rejectedData, setRejectedData] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [mappingColumns, setMappingColumns] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [mappingValidationMessage, setMappingValidationMessage] = useState("");
  const [reviewingUpload, setReviewingUpload] = useState(false);
  const [isImportingFromProcore, setIsImportingFromProcore] = useState(false);
  const [reviewSessionKey, setReviewSessionKey] = useState(0);

  const resetState = () => {
    setIsMappingModalOpen(false);
    setIsReviewModalOpen(false);
    setAcceptedData([]);
    setRejectedData([]);
    setFileList([]);
    setMappingColumns([]);
    setMappingRows([]);
    setColumnMapping({});
    setMappingValidationMessage("");
    setReviewingUpload(false);
    setIsImportingFromProcore(false);
    setReviewSessionKey(0);
  };

  const handleClose = () => {
    resetState();
    onCancel?.();
  };

  const handleBackToUpload = () => {
    setIsMappingModalOpen(false);
    setMappingColumns([]);
    setMappingRows([]);
    setColumnMapping({});
    setMappingValidationMessage("");
    setFileList([]);
  };

  const handleBackFromReview = () => {
    setIsReviewModalOpen(false);
    setAcceptedData([]);
    setRejectedData([]);

    if (mappingRows.length > 0 && mappingColumns.length > 0) {
      setIsMappingModalOpen(true);
      return;
    }

    setIsMappingModalOpen(false);
    setFileList([]);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleMappingChange = (fieldKey, value) => {
    setColumnMapping((currentMapping) => ({
      ...currentMapping,
      [fieldKey]: value ?? "",
    }));
    setMappingValidationMessage("");
  };

  const openReviewModal = (response, syncToken) => {
    setAcceptedData(normalizeScheduleImportRows(response?.validItems));
    setRejectedData(normalizeScheduleImportRows(response?.invalidItems));
    setIsMappingModalOpen(false);
    setReviewSessionKey((currentKey) => currentKey + 1);
    setIsReviewModalOpen(true);
    setFileList([]);
    message.success("File processed successfully!");
    finishSyncActivity?.(syncToken, "Schedule file reviewed");
  };

  const openMappingModal = (response, syncToken) => {
    const uploadedRows = extractScheduleUploadRows(response);

    if (uploadedRows.length === 0) {
      const uploadErrorMessages = extractUploadErrorMessages(response);

      message.error(
        uploadErrorMessages[0] ||
          "No schedule rows found in the uploaded file.",
      );
      failSyncActivity?.(
        syncToken,
        "Failed to process the uploaded schedule file.",
      );
      return;
    }

    const columnOptions = buildScheduleImportColumnOptionsFromRows(
      uploadedRows,
      response?.uploadedColsSet,
    );

    if (columnOptions.length === 0) {
      message.error("We couldn't detect any columns in the uploaded file.");
      failSyncActivity?.(
        syncToken,
        "Failed to detect schedule columns in the uploaded file.",
      );
      return;
    }

    const initialColumnMapping =
      buildInitialScheduleColumnMapping(columnOptions);

    setMappingRows(uploadedRows);
    setMappingColumns(columnOptions);
    setColumnMapping(initialColumnMapping);
    setMappingValidationMessage("");
    setAcceptedData([]);
    setRejectedData([]);
    setIsReviewModalOpen(false);
    setIsMappingModalOpen(true);
    finishSyncActivity?.(syncToken, "Schedule file ready for mapping");
    return null;
  };

  const submitScheduleRowsForReview = (
    mappedRows,
    syncToken = beginSyncActivity?.("Reviewing schedule upload"),
  ) => {
    const requestData = {
      company_id,
      project_id: id,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    };
    const formData = new FormData();

    formData.append("f_schedule", new Blob([]), "");
    formData.append("saveFlag", "0");
    formData.append(
      "scheduleRows",
      JSON.stringify(mappedRows.map(mapScheduleImportRowForApi)),
    );

    return ProjectServices.uploadScheduleFileForReview(requestData, formData)
      .then((response) => normalizeScheduleUploadResponse(response))
      .then((response) => {
        openReviewModal(response, syncToken);
        return response;
      })
      .catch((error) => {
        const errorResponse = extractScheduleUploadErrorResponse(error);

        if (Array.isArray(errorResponse?.uploadedItems)) {
          openMappingModal(errorResponse, syncToken);
          return;
        }

        console.error(error);
        message.error(
          error?.message ||
            "Failed to process the schedule file. Please try again.",
        );
        failSyncActivity?.(
          syncToken,
          "Failed to review the schedule file. You may no longer be synced to the latest version.",
        );
        throw error;
      });
  };

  const handleContinueToReview = () => {
    const mappingValidation = validateScheduleColumnMapping(columnMapping);

    if (!mappingValidation.isValid) {
      setMappingValidationMessage(
        buildMappingValidationMessage(mappingValidation),
      );
      return;
    }

    const mappedRows = buildScheduleImportRowsFromColumnMapping(
      mappingRows,
      columnMapping,
    );

    if (mappedRows.length === 0) {
      setMappingValidationMessage(
        "No schedule rows were found after applying your column mapping.",
      );
      return;
    }

    setReviewingUpload(true);
    submitScheduleRowsForReview(mappedRows)
      .catch(() => {})
      .finally(() => {
        setReviewingUpload(false);
      });
  };

  const handleImportFromProcore = () => {
    const syncToken = beginSyncActivity?.("Importing schedule from Procore");
    const requestData = {
      company_id,
      project_id: id,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    };

    setReviewingUpload(true);
    setIsImportingFromProcore(true);

    ProjectServices.getProcoreSchedule(requestData)
      .then((response) => {
        const procoreRows = buildProcoreScheduleRows(response);

        if (procoreRows.length === 0) {
          message.error("No schedule items were found in Procore.");
          failSyncActivity?.(
            syncToken,
            "No schedule items were found in Procore.",
          );
          return null;
        }

        // The review endpoint expects the file field even when rows come from Procore.
        return submitScheduleRowsForReview(procoreRows, syncToken).catch(
          () => null,
        );
      })
      .catch((error) => {
        console.error(error);
        message.error(
          error?.message ||
            "Failed to import the schedule from Procore. Please try again.",
        );
        failSyncActivity?.(
          syncToken,
          "Failed to import the schedule from Procore. You may no longer be synced to the latest version.",
        );
      })
      .finally(() => {
        setReviewingUpload(false);
        setIsImportingFromProcore(false);
      });
  };

  const handleUpload = (file) => {
    const syncToken = beginSyncActivity?.("Uploading schedule");
    const requestData = {
      company_id,
      project_id: id,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    };
    const formData = new FormData();

    formData.append("f_schedule", file);
    formData.append("saveFlag", "0");
    formData.append("scheduleRows", JSON.stringify([]));

    setReviewingUpload(true);

    ProjectServices.uploadScheduleFileForReview(requestData, formData)
      .then((response) => normalizeScheduleUploadResponse(response))
      .then((response) => {
        if (isScheduleMappingResponse(response)) {
          return openMappingModal(response, syncToken);
        }

        if (isScheduleReviewResponse(response)) {
          openReviewModal(response, syncToken);
          return;
        }

        if (
          Array.isArray(response?.invalidItems) &&
          response.invalidItems.length > 0
        ) {
          const uploadErrorMessages = extractUploadErrorMessages(response);

          message.error(
            uploadErrorMessages[0] ||
              "Failed to process the schedule file. Please try again.",
          );
          failSyncActivity?.(
            syncToken,
            "Failed to process the uploaded schedule file.",
          );
          return;
        }

        return openMappingModal(response, syncToken);
      })
      .catch((error) => {
        const errorResponse = extractScheduleUploadErrorResponse(error);

        if (Array.isArray(errorResponse?.uploadedItems)) {
          openMappingModal(errorResponse, syncToken);
          return;
        }

        console.error(error);
        message.error(
          error?.message ||
            "Failed to process the schedule file. Please try again.",
        );
        failSyncActivity?.(
          syncToken,
          "Failed to upload the schedule file. You may no longer be synced to the latest version.",
        );
      })
      .finally(() => {
        setReviewingUpload(false);
      });

    return false;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList,
    accept: ".csv,.xlsx,.xer,.xml",
    disabled: reviewingUpload,
    beforeUpload: (file) => {
      if (!isAllowedScheduleUploadFile(file)) {
        message.error("You can only upload CSV, XLSX, XER, or XML files!");
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      handleUpload(file);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <>
      <Modal
        title="Import Schedule"
        centered
        destroyOnClose
        open={open && !isMappingModalOpen && !isReviewModalOpen}
        onCancel={handleClose}
        footer={null}
        width={600}
      >
        <div className="py-1">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: "48px", color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for CSV, Excel (.xlsx), XER, and XML files
            </p>
          </Dragger>

          <div className="mt-3 text-center">
            <p>
              <a
                href="/KoncurentScheduleTemplate.csv"
                download="KoncurentScheduleTemplate.csv"
              >
                Download Template
              </a>
            </p>
          </div>

          <Divider plain>or</Divider>

          <div className="d-flex justify-content-center">
            <Button
              disabled={reviewingUpload}
              loading={isImportingFromProcore}
              onClick={handleImportFromProcore}
            >
              <img
                src={ProcoreLogo}
                alt="Procore"
                width={16}
                className="me-2"
              />
              Import from Procore
            </Button>
          </div>

          <div
            className="mt-3 text-center text-danger"
            style={{ fontSize: 12 }}
          >
            * Uploading/importing a new schedule will overwrite any previously
            uploaded schedules
          </div>
        </div>
      </Modal>

      <ScheduleColumnMappingModal
        open={open && isMappingModalOpen}
        availableColumns={mappingColumns}
        columnMapping={columnMapping}
        validationMessage={mappingValidationMessage}
        onBack={handleBackToUpload}
        onCancel={handleClose}
        onContinue={handleContinueToReview}
        onMappingChange={handleMappingChange}
        continuing={reviewingUpload}
      />

      <Modal
        title="Review & finalize"
        centered
        destroyOnClose
        open={open && isReviewModalOpen}
        onCancel={handleClose}
        footer={null}
        width={1400}
      >
        <ReviewScheduleFile
          key={reviewSessionKey}
          rejectedData={rejectedData}
          acceptedData={acceptedData}
          handleBack={handleBackFromReview}
          handleCancel={handleClose}
          setAcceptedData={setAcceptedData}
          setRejectedData={setRejectedData}
          onImportSuccess={onImportSuccess}
          beginSyncActivity={beginSyncActivity}
          finishSyncActivity={finishSyncActivity}
          failSyncActivity={failSyncActivity}
        />
      </Modal>
    </>
  );
}

export default ImportSchedule;
